import React, { useState, useEffect } from "react";
import { Store, Palette, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { submitStoreRequest, getSellerStatus } from "../lib/hub";
import { PageHero, Field, SuccessCard } from "../components/FormKit";
import { EXTERNAL_LINKS } from "../config";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const BENEFITS = ["Access to a growing marketplace", "Product visibility", "Business support", "Seller tools", "Marketplace branding", "Growth opportunity", "Professional customer experience", "Early seller advantage"];
const SELLER_TYPES = ["Independent shop", "Online boutique", "Product supplier", "Local merchant", "Beauty seller", "Fashion seller", "Home goods seller", "Digital/education resource seller", "Approved marketplace seller"];

export const BecomeSeller = () => {
  const [f, setF] = useState({ full_name: "", business_name: "", shop_name: "", email: "", phone: "", country: "", website_store_link: "", social_media_link: "", product_category: "", number_of_products: "", already_sells_online: "", current_sales_channels: "", seller_type: "", message: "" });
  const [done, setDone] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const [sc, setSc] = useState({ store_name: "", seller_name: "", brand_colors: "", style: "modern", categories: "", target_customers: "", description: "" });
  const [scDone, setScDone] = useState(false);
  const setS = (k, v) => setSc({ ...sc, [k]: v });

  const { user } = useAuth();
  const [alreadySeller, setAlreadySeller] = useState(false);
  useEffect(() => { if (user) getSellerStatus().then((s) => setAlreadySeller(s.is_seller)).catch(() => {}); }, [user]);

  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/leads/shop-seller", f); setDone(true); toast.success("Application received"); window.scrollTo({ top: 0, behavior: "smooth" }); }
    catch { toast.error("Please check your details and try again."); }
  };

  const submitStore = async (e) => {
    e.preventDefault();
    if (!sc.store_name.trim() || !sc.seller_name.trim()) { toast.error("Store name and your name are required."); return; }
    try {
      await submitStoreRequest({
        seller_name: sc.seller_name, store_name: sc.store_name, style: sc.style,
        brand_colors: sc.brand_colors ? sc.brand_colors.split(",").map((c) => c.trim()).filter(Boolean) : null,
        categories: sc.categories ? sc.categories.split(",").map((c) => c.trim()).filter(Boolean) : null,
        target_customers: sc.target_customers, description: sc.description,
      });
      setScDone(true); toast.success("Store request received");
      document.getElementById("store-creator")?.scrollIntoView({ behavior: "smooth" });
    } catch { toast.error("Could not submit your store request."); }
  };

  return (
    <div data-testid="become-seller-page">
      <PageHero eyebrow="Seller Commerce Hub" title="Sell on TAVEN" subtitle="Join a marketplace ecosystem built for sellers, creators, entrepreneurs, and growing businesses.">
        <a href={EXTERNAL_LINKS.SELLER_LOGIN_URL} target="_blank" rel="noreferrer" data-testid="seller-login-btn" className="border border-white/30 text-white px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-white hover:text-black transition-colors">Seller Login</a>
        <a href={EXTERNAL_LINKS.SELLER_DASHBOARD_URL} target="_blank" rel="noreferrer" data-testid="seller-dashboard-btn" className="border border-taven-gold text-taven-gold px-6 py-3 font-bold uppercase tracking-wider text-sm hover:bg-taven-gold hover:text-black transition-colors">Seller Dashboard</a>
      </PageHero>

      {alreadySeller && (
        <div className="bg-green-50 border-b border-green-200 px-4 py-3" data-testid="already-seller-banner">
          <div className="mx-auto max-w-7xl flex flex-wrap items-center gap-3 text-sm text-green-800">
            <Store className="w-4 h-4" /> You already have a TAVEN seller account — no need to apply again.
            <Link to="/seller-dashboard" className="ml-auto bg-taven-black text-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-orange" data-testid="goto-seller-dashboard">Go to Seller Dashboard</Link>
          </div>
        </div>
      )}

      <section className="py-14 px-4 md:px-8 lg:px-12 mx-auto max-w-7xl">
        <p className="text-lg text-muted-foreground max-w-3xl">TAVEN is preparing a seller marketplace where approved merchants can list products, reach customers, and grow within a professional digital ecosystem.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {BENEFITS.map((b) => (
            <div key={b} className="p-5 bg-white border border-black/5"><Sparkles className="w-5 h-5 text-taven-gold" /><p className="text-sm font-semibold mt-3">{b}</p></div>
          ))}
        </div>
      </section>

      {/* Bring your shop to TAVEN */}
      <section className="py-14 bg-white border-y border-black/5">
        <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-taven-gold">Independent Shops Welcome</span>
            <h2 className="text-3xl font-black font-display tracking-tighter mt-1" data-testid="bring-shop-title">Bring Your Shop to TAVEN</h2>
            <p className="text-muted-foreground mt-3">TAVEN is building a marketplace ecosystem where independent shops, boutiques, merchants, and approved suppliers can apply to sell their products to a growing customer base. If you already own a shop or sell products online, you can apply to become a TAVEN seller.</p>
          </div>

          {done ? (
            <SuccessCard testid="seller-success" title="Your seller application has been received." text="TAVEN will review your request." />
          ) : (
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="seller-form">
              <Field label="Full Name" name="full_name" value={f.full_name} onChange={set} required />
              <Field label="Business Name" name="business_name" value={f.business_name} onChange={set} />
              <Field label="Shop Name" name="shop_name" value={f.shop_name} onChange={set} />
              <Field label="Email" name="email" type="email" value={f.email} onChange={set} required />
              <Field label="Phone" name="phone" value={f.phone} onChange={set} />
              <Field label="Country" name="country" value={f.country} onChange={set} />
              <Field label="Website or Store Link" name="website_store_link" value={f.website_store_link} onChange={set} />
              <Field label="Social Media Link" name="social_media_link" value={f.social_media_link} onChange={set} />
              <Field label="Seller Type" name="seller_type" value={f.seller_type} onChange={set} options={SELLER_TYPES} />
              <Field label="Product Category" name="product_category" value={f.product_category} onChange={set} />
              <Field label="Number of Products" name="number_of_products" value={f.number_of_products} onChange={set} />
              <Field label="Do you already sell online?" name="already_sells_online" value={f.already_sells_online} onChange={set} options={["Yes", "No"]} />
              <div className="sm:col-span-2"><Field label="Where do you currently sell?" name="current_sales_channels" value={f.current_sales_channels} onChange={set} /></div>
              <div className="sm:col-span-2"><Field label="Message" name="message" value={f.message} onChange={set} textarea /></div>
              <button className="sm:col-span-2 bg-taven-black text-white font-bold uppercase tracking-wider py-4 hover:bg-taven-orange transition-colors flex items-center justify-center gap-2" data-testid="seller-submit"><Store className="w-4 h-4" /> Apply to Sell on TAVEN</button>
            </form>
          )}
          <p className="text-center text-sm text-muted-foreground mt-6">Ready to expand further? <a href="/sell-on-other-marketplaces" className="text-taven-gold font-semibold underline">Sell on Other Marketplaces →</a></p>
        </div>
      </section>

      {/* Create Your Store */}
      <section id="store-creator" className="py-14 bg-taven-gray/30 border-b border-black/5" data-testid="store-creator-section">
        <div className="px-4 md:px-8 lg:px-12 mx-auto max-w-3xl">
          <div className="text-center mb-8">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-taven-gold">Seller Store Creator</span>
            <h2 className="text-3xl font-black font-display tracking-tighter mt-1" data-testid="store-creator-title">Create Your Store</h2>
            <p className="text-muted-foreground mt-3">Tell us about your brand and our store-design team will prepare a professional, branded storefront for you. Your store starts with no products and nothing is published until you approve it.</p>
          </div>

          {scDone ? (
            <SuccessCard testid="store-creator-success" title="Your store request has been received." text="Our store-design team will review and prepare your storefront. Please allow up to 24 hours for access." />
          ) : (
            <form onSubmit={submitStore} className="grid sm:grid-cols-2 gap-4" data-testid="store-creator-form">
              <Field label="Store Name" name="store_name" value={sc.store_name} onChange={setS} required />
              <Field label="Your Name" name="seller_name" value={sc.seller_name} onChange={setS} required />
              <Field label="Brand Colors (comma separated)" name="brand_colors" value={sc.brand_colors} onChange={setS} placeholder="#111111, #c8a24a" />
              <Field label="Style" name="style" value={sc.style} onChange={setS} options={["modern", "luxury", "minimal", "bold", "playful"]} />
              <div className="sm:col-span-2"><Field label="Categories (comma separated)" name="categories" value={sc.categories} onChange={setS} placeholder="Jewelry, Accessories, Beauty" /></div>
              <div className="sm:col-span-2"><Field label="Target Customers" name="target_customers" value={sc.target_customers} onChange={setS} /></div>
              <div className="sm:col-span-2"><Field label="Store Description" name="description" value={sc.description} onChange={setS} textarea /></div>
              <button className="sm:col-span-2 bg-taven-black text-white font-bold uppercase tracking-wider py-4 hover:bg-taven-orange transition-colors flex items-center justify-center gap-2" data-testid="store-creator-submit"><Palette className="w-4 h-4" /> Build My Store</button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
};

export default BecomeSeller;
